import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiClock, FiMail } = FiIcons;

function Contact() {
  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Phone Number',
      details: '+971 50 426 1841',
      action: 'tel:+971504261841'
    },
    {
      icon: FiMapPin,
      title: 'Address',
      details: 'Al Raha Building - Shop 4, Al Barsha First, Al Barsha, Dubai - UAE',
      action: 'https://maps.app.goo.gl/JcwkAAxgv6HhjercA'
    },
    {
      icon: FiClock,
      title: 'Working Hours',
      details: 'Daily: 9:00 AM - 11:00 PM',
      action: null
    }
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <motion.div 
      className="pt-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Ready to experience premium grooming? Get in touch with us to book your 
              appointment or learn more about our services.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Get in Touch</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We're here to help and answer any questions you might have
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                className="bg-white p-8 rounded-lg shadow-lg text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <SafeIcon icon={info.icon} className="h-12 w-12 text-gray-900 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{info.title}</h3>
                {info.action ? (
                  <a 
                    href={info.action}
                    target={info.action.startsWith('http') ? '_blank' : '_self'}
                    rel={info.action.startsWith('http') ? 'noopener noreferrer' : ''}
                    className="text-gray-600 hover:text-gray-900 transition-colors"
                  >
                    {info.details}
                  </a>
                ) : (
                  <p className="text-gray-600">{info.details}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Find Us</h2>
            <p className="text-gray-600 text-lg">
              Located in the heart of Al Barsha, Dubai
            </p>
          </motion.div>
          
          <motion.div
            className="rounded-lg overflow-hidden shadow-2xl"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3612.573932042649!2d55.20416107472471!3d25.116280221090125!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f6bc007e17021%3A0xf3a970a88ce75f28!2sADRES%20TURKISH%20GENTS%20SALON!5e0!3m2!1sen!2s!4v1764508822037!5m2!1sen!2s" 
              width="100%" 
              height="450" 
              style={{ border: 0 }} 
              allowFullScreen="" 
              loading="lazy" 
              referrerPolicy="no-referrer-when-downgrade"
              title="ADRES Turkish Gents Salon Location"
            ></iframe>
          </motion.div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Visit?</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-8">
              Choose your preferred way to get in touch with us
            </p>
          </motion.div>
          
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
            <motion.a 
              href="tel:+971504261841"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiPhone} className="h-5 w-5" />
              <span>Call Now</span>
            </motion.a>
            
            <motion.a 
              href="https://maps.app.goo.gl/JcwkAAxgv6HhjercA"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors flex items-center space-x-3"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiMapPin} className="h-5 w-5" />
              <span>Get Directions</span>
            </motion.a>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Visit Our Salon</h2>
              <p className="text-gray-600 text-lg mb-6">
                Step into ADRES Turkish Gents Salon and experience a world of premium grooming. 
                Our modern facility combines comfort with functionality, creating the perfect 
                environment for your grooming needs.
              </p>
              <p className="text-gray-600 text-lg mb-6">
                We welcome walk-ins, but we recommend calling ahead to ensure minimal wait time. 
                Our friendly staff is always ready to assist you and answer any questions about our services.
              </p>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiClock} className="h-5 w-5 text-gray-900" />
                  <span className="text-gray-700">Open Daily: 9:00 AM - 11:00 PM</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMapPin} className="h-5 w-5 text-gray-900" />
                  <span className="text-gray-700">Easy parking available</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://i.ibb.co/k2rf0cqg/image-4.jpg" 
                alt="ADRES Turkish Gents Salon Interior"
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
}

export default Contact;